import log from 'loglevel'
import Page from 'commons/page'
import {
  AuthenticationFederationProvider,
  fetchAuthenticationFederationProvider,
  linkAuthenticationFederationProvider,
  unlinkAuthenticationFederationProvider,
} from 'commons/firebase-auth'
import Block from 'components/block'
import styled from 'styled-components'
import { ReactNode, useEffect, useState } from 'react'
import { AuthenticationFederationProviderId } from '../../schema/user'
import { AuthErrorCodes } from 'firebase/auth'
import { FirebaseError } from '@firebase/util'

import BaseDialog from '../../components/dialog'
import Button from '../../components/button'
import { PropertyListTitle, StrongText } from '../../components/typography'

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const InnerWrapper = styled.div`
  width: 400px;
  box-sizing: border-box;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    width: 100%;
    max-width: 400px;
  }
`

const Description = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    font-size: 13px;
  }
`

const AccountContainer = styled.div`
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0 16px;
  box-sizing: border-box;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    padding: 0 12px;
  }
`

const SettingItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    padding: 12px 0;
  }
`

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    gap: 4px;
  }
`

const ProviderName = styled.div`
  font-size: ${(p) => p.theme.font.size.m};
  font-weight: 500;
  color: #333;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    font-size: 14px;
  }
`

const StatusBadge = styled.div<{ enabled: boolean }>`
  font-size: 14px;
  color: ${(props) => (props.enabled ? '#666' : '#999')};
  background-color: ${(props) => (props.enabled ? '#E6F6EB' : '#f5f5f5')};
  padding: 4px 12px;
  border-radius: 4px;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    font-size: 12px;
    padding: 3px 8px;
  }
`

const ActionButton = styled.div<{ enabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: ${(props) => (props.enabled ? '#d32f2f' : '#ff8c00')};
  cursor: pointer;
  font-weight: 500;
  user-select: none;

  &:hover {
    opacity: 0.7;
  }

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    font-size: 13px;
  }
`

const ErrorMessage = styled(StrongText)`
  color: red;
`

type FederationTarget = {
  componentKey: string
  providerName: string
  authenticationProviderId: AuthenticationFederationProviderId
  enabled: boolean
  logo?: ReactNode
  linkedAccountMail?: string | null
}

const federationTarget: FederationTarget[] = [
  {
    componentKey: 'google',
    providerName: 'Google',
    authenticationProviderId: 'google.com',
    enabled: false,
  },
  {
    componentKey: 'facebook',
    providerName: 'Facebook',
    authenticationProviderId: 'facebook.com',
    enabled: false,
  },
  {
    componentKey: 'twitter',
    providerName: 'X（旧ツイッター）',
    authenticationProviderId: 'twitter.com',
    enabled: false,
  },
]

const ButtonArea = styled.div`
  gap: 20px;
  padding-top: 0px;
  display: flex;
`

const CloseButton = styled(Button)`
  width: 100%;
  background-color: white;
  color: black;
`
const ExecuteButton = styled(Button)`
  width: 100%;
`

const AuthenticationSetting = () => {
  const [userSetting, setUserSetting] = useState<
    AuthenticationFederationProvider[] | null
  >()
  const [setting, setSetting] = useState<FederationTarget[]>([])

  useEffect(() => {
    ;(async () => {
      const result = fetchAuthenticationFederationProvider()
      setUserSetting(result)
    })()
  }, [])

  useEffect(() => {
    if (!userSetting) {
      return
    }

    setSetting(
      federationTarget.map((item) => {
        const foundSetting = userSetting.find(
          (enabledItem) =>
            enabledItem.providerId === item.authenticationProviderId
        )

        return {
          ...item,
          enabled: !!foundSetting,
          linkedAccountMail: foundSetting?.linkedAccountMail,
        }
      })
    )
  }, [userSetting])

  const linkedUpdateSetting = (provider: AuthenticationFederationProvider) => {
    const updatedSetting = setting.map((item) => {
      if (item.authenticationProviderId === provider.providerId) {
        return {
          ...item,
          enabled: true,
          linkedAccountMail: provider.linkedAccountMail,
        }
      }
      return item
    })
    setSetting(updatedSetting)
  }

  const unLinkedUpdateSetting = (
    providerId: AuthenticationFederationProviderId
  ) => {
    const updatedSetting = setting.map((item) => {
      if (item.authenticationProviderId === providerId) {
        return { ...item, enabled: false, linkedAccountMail: null }
      }
      return item
    })
    setSetting(updatedSetting)
  }

  const linkSocialAccount = async (
    key: string,
    close: () => void,
    showError: () => void
  ) => {
    const providerId = key as AuthenticationFederationProviderId
    try {
      const linkedResult = await linkAuthenticationFederationProvider(
        providerId
      )

      linkedUpdateSetting(linkedResult)
      close()
    } catch (e: unknown) {
      log.error(e)
      if (e instanceof FirebaseError) {
        if (
          (e as FirebaseError).code === AuthErrorCodes.CREDENTIAL_ALREADY_IN_USE
        ) {
          showError()
        }
      }
    }
  }
  const unLinkSocialAccount = async (
    key: string,
    close: () => void,
    showError: () => void
  ) => {
    const providerId = key as AuthenticationFederationProviderId

    try {
      await unlinkAuthenticationFederationProvider(providerId)
      unLinkedUpdateSetting(providerId)
      close()
    } catch (e: unknown) {
      if (e instanceof FirebaseError) {
        log.error(e)
        if ((e as FirebaseError).code === AuthErrorCodes.NO_SUCH_PROVIDER) {
          showError()
        }
      }
    }
  }

  const ConfirmDialogBody = (props: {
    body: ReactNode
    executeLabel: string
    onClose: () => void
    onExecute: () => Promise<void>
  }) => {
    return (
      <>
        <Block>
          <StrongText size={'md'}>{props.body}</StrongText>
        </Block>
        <Block>
          <ButtonArea>
            <CloseButton onClick={props.onClose}>
              <span>閉じる</span>
            </CloseButton>
            <ExecuteButton onClick={props.onExecute}>
              <span>{props.executeLabel}</span>
            </ExecuteButton>
          </ButtonArea>
        </Block>
      </>
    )
  }

  const ErrorDialogBody = (props: { body: ReactNode; onClose: () => void }) => {
    return (
      <>
        <Block>{props.body}</Block>
        <Block>
          <ButtonArea>
            <CloseButton onClick={props.onClose}>
              <span>閉じる</span>
            </CloseButton>
          </ButtonArea>
        </Block>
      </>
    )
  }

  //設定/解除Area
  const SettingRow = ({ item }: { item: FederationTarget }) => {
    const [showConfirm, setShowConfirm] = useState(false)
    const [showError, setShowError] = useState(false)

    const onClickAction = () => {
      setShowConfirm(true)
    }

    const onConfirmClose = () => {
      setShowConfirm(false)
    }

    const onErrorClose = () => {
      setShowError(false)
      setShowConfirm(false)
    }

    const showErrorDialog = () => {
      setShowError(true)
    }

    const ConfirmDialog = (props: {
      children: ReactNode
      show: boolean
      onClose: () => void
    }) => {
      return (
        <BaseDialog
          show={props.show}
          onClose={props.onClose}
          styleOptions={{
            height: 130,
            position: 'top',
          }}
        >
          {props.children}
        </BaseDialog>
      )
    }

    const ErrorDialog = (props: {
      children: ReactNode
      show: boolean
      onClose: () => void
    }) => {
      return (
        <BaseDialog
          show={props.show}
          onClose={props.onClose}
          styleOptions={{
            height: 190,
            position: 'top',
          }}
        >
          {props.children}
        </BaseDialog>
      )
    }

    return (
      <>
        <SettingItem>
          <LeftSection>
            <ProviderName>{item.providerName}</ProviderName>
            <StatusBadge enabled={item.enabled}>
              {item.enabled ? '登録済' : '未登録'}
            </StatusBadge>
          </LeftSection>
          <ActionButton enabled={item.enabled} onClick={onClickAction}>
            {item.enabled ? (
              <>
                登録解除 <span>×</span>
              </>
            ) : (
              <>
                登録 <span>{'>'}</span>
              </>
            )}
          </ActionButton>
        </SettingItem>
        <ConfirmDialog show={showConfirm} onClose={onConfirmClose}>
          <ConfirmDialogBody
            body={`${item.providerName}ログインを${
              item.enabled ? '解除' : '設定'
            }しますか？`}
            executeLabel={item.enabled ? '解除' : '設定'}
            onClose={onConfirmClose}
            onExecute={() =>
              item.enabled
                ? unLinkSocialAccount(
                    item.authenticationProviderId,
                    onConfirmClose,
                    showErrorDialog
                  )
                : linkSocialAccount(
                    item.authenticationProviderId,
                    onConfirmClose,
                    showErrorDialog
                  )
            }
          />
        </ConfirmDialog>
        <ErrorDialog show={showError} onClose={onErrorClose}>
          {item.enabled ? (
            <ErrorDialogBody
              body={
                <ErrorMessage size={'md'}>
                  予期せぬエラーが発生したためアカウントを設定できませんでした。
                  <br />
                  <br />
                  改善されない場合は担当エージェントにご連絡ください。
                </ErrorMessage>
              }
              onClose={onErrorClose}
            />
          ) : (
            <ErrorDialogBody
              body={
                <ErrorMessage size={'md'}>
                  このアカウントは他のアカウントに紐づけられています。
                  <br />
                  <br />
                  以前の設定を解除してから実施してください。
                </ErrorMessage>
              }
              onClose={onErrorClose}
            />
          )}
        </ErrorDialog>
      </>
    )
  }
  return (
    <Page title="ログイン設定" type="user">
      <ContentWrapper>
        <InnerWrapper>
          <PropertyListTitle>ログイン設定</PropertyListTitle>
          <Description>
            ログインできるアカウントを設定してください。
          </Description>
          <AccountContainer>
            {setting.map((item) => (
              <SettingRow
                key={`setting-row-${item.authenticationProviderId}`}
                item={item}
              />
            ))}
          </AccountContainer>
        </InnerWrapper>
      </ContentWrapper>
    </Page>
  )
}

export default AuthenticationSetting
